// Third-party
import React, { ReactElement, createRef } from 'react';

// Components
import SearchInput from './SearchInput';
import { FormattedMessage } from '../../../utils/i18n';
import { LoadingSpinner } from '../common';
import classNames from 'classnames';
import DropdownMenu from './DropdownMenu';

type Props = {
  onSelect: (value: any) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  compact?: boolean;
  onSubmit?: () => void;
  dark?: boolean;
  children: ReactElement[];
  filterOption: (option: ReactElement, query: string) => boolean;
  additionnalOptionsBefore?: ReactElement[];
  additionnalOptionsAfter?: ReactElement[];
  selectedOptionValue?: any;
  getTextForOptionValue?: (option: any) => string | undefined;
  onSearch: (query: string) => void;
  loadingResults?: boolean;
  label?: string;
  toggleControl?: ReactElement;
};

type State = {
  inputText: string;
  showInput: boolean;
};

class SearchSelectInput extends React.Component<Props, State> {
  private inputRef = createRef<SearchInput>();

  private dropdownRef = createRef<DropdownMenu>();

  readonly state: State = {
    inputText: '',
    showInput: false,
  };

  static defaultProps = {
    onSearch: () => {},
  };

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  componentDidUpdate() {
    const { showInput } = this.state;

    if (this.inputRef.current && showInput) {
      this.inputRef.current.focusInput();
    }
  }

  handleClickOutside = (event: Event) => {
    if (!(event.target instanceof Node)) {
      this.onFocus(false);
      return;
    }

    const containerRef = this.dropdownRef.current?.getMenuRef();
    const inputRef = this.inputRef.current?.input.current?.inputContainer.current;

    if (!containerRef?.contains(event.target) && !inputRef?.contains(event.target)) {
      this.onFocus(false);
    }
  };

  forceMenuDisplay = () => {
    const { inputText, showInput } = this.state;
    const { selectedOptionValue } = this.props;

    if (inputText === '' && showInput && !selectedOptionValue) {
      return true;
    }

    if (!showInput) {
      return false;
    }

    return undefined;
  };

  getSelectedOption = (targetValue: any) =>
    this.props.children.find(c => {
      const { value } = c.props;
      return value ? value === targetValue : false;
    });

  getSelectedOptionIndex = (targetValue: any): number | undefined => {
    const selectedOption = this.getSelectedOption(targetValue);
    if (selectedOption) {
      return this.filterOptions().indexOf(selectedOption);
    }

    return undefined;
  };

  getTextForOption = (option?: ReactElement) => {
    let text = '';

    if (option && option.props) {
      const { value, label } = option.props;
      if (value) {
        const { getTextForOptionValue } = this.props;
        const newText = label || (getTextForOptionValue && getTextForOptionValue(value));
        if (newText) {
          text = newText;
        }
      }
    }

    return text;
  };

  onSelectItem = (option?: ReactElement) => {
    if (option && option.props) {
      const { value } = option.props;
      this.props.onSelect(value);
      let newState = {};
      if (!value) {
        newState = { showInput: true, inputText: '' };
      } else {
        newState = { showInput: false };
      }
      this.setState(newState);
    } else {
      this.setState({ showInput: false }, () => {
        if (document && document.activeElement) {
          (document.activeElement as HTMLElement).blur();
        }
      });
    }
  };

  onChangeInput = (value: string) => {
    this.setState({ inputText: value });
    this.props.onSearch(value);
  };

  onFocus = (focus: boolean) => {
    this.setState({ showInput: focus });
  };

  filterOptions = () => {
    const { children, filterOption, additionnalOptionsBefore, additionnalOptionsAfter, loadingResults } = this.props;
    const { inputText } = this.state;
    let options: ReactElement[] = children;

    if (loadingResults) {
      return [
        <div key="spinner" className="dropdown-empty flex-fill">
          <LoadingSpinner />
        </div>,
      ];
    }

    if (inputText) {
      options = children.filter(option => filterOption(option, inputText));
    }

    if (!options.length) {
      options.push(
        <div className="dropdown-empty flex-fill">
          <FormattedMessage id="EmptyState.NoResults.Title" />
        </div>,
      );
    }

    if (additionnalOptionsBefore) {
      options = additionnalOptionsBefore.concat(options);
    }

    if (additionnalOptionsAfter) {
      options = options.concat(additionnalOptionsAfter);
    }

    return options;
  };

  getChildComponent = () => {
    const { selectedOptionValue, dark, onSubmit, compact, disabled, placeholder, className } = this.props;
    const { showInput, inputText } = this.state;

    const selected = this.getSelectedOption(selectedOptionValue);

    if (selected && !showInput) {
      return (
        <div className="search-select-input-selection-container">
          <button type="button" className="search-select-input-selection" onClick={() => this.setState({ showInput: true })}>
            <span>{selected}</span>
          </button>
        </div>
      );
    }

    return (
      <SearchInput
        ref={this.inputRef}
        data-private
        data-dd-privacy="mask"
        dark={dark}
        onSubmit={onSubmit}
        compact={compact}
        className={className}
        disabled={disabled}
        placeholder={placeholder}
        value={inputText}
        onChange={this.onChangeInput}
        onFocus={() => this.onFocus(true)}
      />
    );
  };

  render() {
    const { label, toggleControl } = this.props;
    const labelClassName = label || toggleControl ? 'label-container' : '';
    return (
      // TODO: Label implementation with Downshift needs to change for accessibility reasons.
      <div>
        <div className={classNames(labelClassName)}>
          {toggleControl && toggleControl}
          {label && <label>{label}</label>}
        </div>

        <DropdownMenu
          ref={this.dropdownRef}
          isExtended={this.forceMenuDisplay()}
          className="search-select-input"
          options={this.filterOptions()}
          onSelect={this.onSelectItem}
          selected={this.getSelectedOptionIndex(this.props.selectedOptionValue)}
        >
          {this.getChildComponent()}
        </DropdownMenu>
      </div>
    );
  }
}

export default SearchSelectInput;
